import { useEffect, useState } from "react";
import { MapPin } from "lucide-react";
import { fetchMarketTier } from "@/lib/api";

const TIER_LABEL = {
    high_cost: "High-cost",
    above_avg: "Above average",
    average: "Average",
    below_avg: "Below average",
};

const TIER_TINT = {
    high_cost: "bg-[var(--bl-amber-tint)] text-[var(--bl-amber)]",
    above_avg: "bg-[var(--bl-teal-tint)] text-bl-primary",
    average: "bg-[var(--bl-stone-soft)] text-bl",
    below_avg: "bg-[var(--bl-moss-tint)] text-[var(--bl-moss)]",
};

/**
 * Compact "market tier" badge for the Closing Cost + Repair Exposure tools.
 * Calls /api/tools/market-tier with the buyer's ZIP and renders a one-line
 * summary of their market's labor + material premium over the national avg.
 *
 * Renders NOTHING when:
 *   • No ZIP provided
 *   • Backend reports `known=false` (we don't have data for that ZIP — we'd
 *     rather show no badge than a misleading "average" badge with no source)
 */
export default function MarketTierBadge({ zipCode, className = "" }) {
    const [data, setData] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const z = (zipCode || "").trim();
        if (z.length !== 5 || !/^\d{5}$/.test(z)) {
            setData(null);
            return;
        }
        (async () => {
            try {
                const r = await fetchMarketTier(z);
                if (!cancelled) setData(r);
            } catch {
                if (!cancelled) setData(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [zipCode]);

    if (!data) return null;
    // Even when the specific ZIP isn't in MARKET_PROFILES (`known=false`),
    // we may still have a state-level coverage signal worth surfacing — that's
    // the whole point of `low_state_coverage`. Show the warning banner alone
    // (without the misleading "Average market" pill) so buyers in MT/SD/ND
    // see the honest framing instead of silence.
    if (!data.known) {
        if (!data.low_state_coverage) return null;
        return (
            <div
                className={`rounded-lg border border-bl bg-bl-bg p-3 flex items-start gap-2.5 ${className}`}
                data-testid="market-tier-badge"
            >
                <MapPin className="h-4 w-4 text-bl-primary flex-shrink-0 mt-0.5" />
                <div className="text-xs flex-1 min-w-0">
                    <div
                        className="text-[11px] leading-snug text-[var(--bl-amber)] bg-[var(--bl-amber-tint)] rounded-md px-2 py-1.5"
                        data-testid="market-tier-low-coverage"
                        title={`Our localized dataset covers ~${data.state_coverage_pct}% of ZIP prefixes in ${data.state}. The confidence band on individual repair estimates may be wider than usual for this region.`}
                    >
                        Limited localized data for{" "}
                        <span className="font-medium">{data.state}</span>
                        {" "}({data.state_coverage_pct}% of ZIPs covered).
                        Estimates fall back to the national average — treat
                        ranges as planning guidance, not bids.
                    </div>
                </div>
            </div>
        );
    }

    const label = TIER_LABEL[data.tier] || "Localized";
    const tint = TIER_TINT[data.tier] || TIER_TINT.average;
    const laborPctText =
        data.labor_premium_pct > 0
            ? `+${data.labor_premium_pct}%`
            : `${data.labor_premium_pct}%`;
    const matPctText =
        data.material_premium_pct > 0
            ? `+${data.material_premium_pct}%`
            : `${data.material_premium_pct}%`;

    return (
        <div
            className={`rounded-lg border border-bl bg-bl-bg p-3 flex items-start gap-2.5 ${className}`}
            data-testid="market-tier-badge"
        >
            <MapPin className="h-4 w-4 text-bl-primary flex-shrink-0 mt-0.5" />
            <div className="text-xs flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                    <span
                        className={`text-[10px] tracking-wide uppercase font-medium px-1.5 py-0.5 rounded ${tint}`}
                        data-testid="market-tier-label"
                    >
                        {label} market
                    </span>
                    <span className="text-bl-muted">·</span>
                    <span className="text-bl font-medium truncate">
                        {data.name}
                    </span>
                </div>
                <div className="text-bl-muted mt-1">
                    Localized premium over national average:{" "}
                    <span className="text-bl font-medium tnum">
                        {laborPctText} labor
                    </span>
                    {" · "}
                    <span className="text-bl font-medium tnum">
                        {matPctText} materials
                    </span>
                </div>
                {data.low_state_coverage && (
                    <div
                        className="mt-2 text-[11px] leading-snug text-[var(--bl-amber)] bg-[var(--bl-amber-tint)] rounded-md px-2 py-1.5"
                        data-testid="market-tier-low-coverage"
                        title={`Our localized dataset covers ~${data.state_coverage_pct}% of ZIP prefixes in ${data.state}. The confidence band on individual repair estimates may be wider than usual for this region.`}
                    >
                        Limited localized data for{" "}
                        <span className="font-medium">{data.state}</span>
                        {" "}({data.state_coverage_pct}% of ZIPs covered). Confidence
                        bands may be wider for this region — treat ranges as
                        planning guidance, not bids.
                    </div>
                )}
            </div>
        </div>
    );
}
